import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Wrapper } from '../../styles/SearchBooksStyle';
import { getListOfBooksByAgeGroup, searchBooksByQuery } from '../../../controller/bookSelection/bookSelectionController';
import { StandardSlidingPillNav, ScratchBookForm } from '../../../components';
import { setSelectedBook } from '../../../slices/books/bookSelectionSlice';

const contentShellStyle = {
  width: '100%',
  padding: '0.75rem clamp(0.5rem, 3vw, 1.25rem) 1rem',
};

const SearchOwnBooks = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // React Router v6 hook to read query params
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category') || '';
  const OS = searchParams.get("OS") || "";
  const subscriptionType = (searchParams.get('subscriptionType') || '').toLowerCase();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOpen, setSortOpen] = useState(false);
  const [sortBy, setSortBy] = useState('popular');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [premiumView, setPremiumView] = useState('scratch');
  

  const { bookList, totalBook, next, previous, isLoading, error } = useSelector(
    (state) => state.bookSelection
  );
  const premiumUser = subscriptionType === 'premium';

  // Load books on mount or when category/sort/page changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      dispatch(getListOfBooksByAgeGroup({
        ageGroup: category,
        sortBy,
        page,
        pageSize: rowsPerPage
      }));
    }
  }, [dispatch, category, sortBy, searchQuery, page, rowsPerPage]);

  const performSearch = () => {
    if (searchQuery.trim()) {
      dispatch(searchBooksByQuery({
        query: searchQuery,
        page,
        pageSize: rowsPerPage
      }));
    }
  };

  const handlePrev = () => {
    if (previous) setPage((p) => p - 1);
  };

  const handleNext = () => {
    if (next) setPage((p) => p + 1);
  };

  const handleBookSelected = (bookId) => {
    navigate(`/books/preview-own/${bookId}`, {
      state: {
        OS
      }
    });
  };

  const handleCuratedBookSubmit = (book) => {
    const selectedBookPayload = {
      title: book.title,
      authors: [{ name: book.author }],
      formats: {
        'image/jpeg': book.image || null,
      },
      paragraphText: book.paragraphs.map((item) => item.paragraph).join('\n\n'),
      level: book.level,
    };

    dispatch(setSelectedBook(selectedBookPayload));
    navigate('/books/preview-own/scratch', {
      state: {
        OS,
        skipFetch: true,
      },
    });
  };

  const renderSearchView = () => (
    <Wrapper>
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search for books..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') performSearch();
          }}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')}>✕</button>
        )}
        <button onClick={performSearch}>🔍</button>
      </div>

      <div className="sort-button-wrapper">
        <button onClick={() => setSortOpen((o) => !o)}>Sort</button>
        {sortOpen && (
          <>
            <div className="overlay" onClick={() => setSortOpen(false)} />
            <div className="sort-popup">
              <div className="sort-header">
                <span>Sort By</span>
                <button onClick={() => setSortOpen(false)}>✕</button>
              </div>
              {['popular', 'fiction', 'history', 'horror'].map((opt) => (
                <button
                  key={opt}
                  onClick={() => {
                    setSortBy(opt);
                    setSortOpen(false);
                  }}
                >
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {isLoading && <p>Loading...</p>}
      {error && <p>Error: {error}</p>}

      <div className="book-grid">
        {!isLoading &&
          bookList.map((book) => (
            <div
              className="book-card"
              key={book.id}
              onClick={() => handleBookSelected(book.id)}
            >
              <div className="cover">
                {book.formats['image/jpeg'] ? (
                  <img
                    src={book.formats['image/jpeg']}
                    alt={book.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <span>No Image</span>
                )}
              </div>
              <div className="meta">
                <p className="title">{book.title}</p>
                <p className="author">
                  <strong className="book-desc-label">Author</strong>: {book.authors[0]?.name || 'Unknown'}
                </p>
                <p className="copyright">
                  Copyright: {book.copyright ? 'Yes' : 'No'}
                </p>
              </div>
            </div>
          ))}
      </div>

      <div className="pagination">
        <label>
          Rows per page:
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            {[50, 100, 150].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <span>
          Page {page} • {totalBook} total
        </span>

        <button onClick={handlePrev} disabled={!previous}>
          {'<'} Prev
        </button>
        <button onClick={handleNext} disabled={!next}>
          Next {'>'}
        </button>
      </div>
    </Wrapper>
  );

  return (
    <div style={contentShellStyle}>
      {premiumUser && (
        <div style={{ marginBottom: '1rem' }}>
          <StandardSlidingPillNav
            options={[
              { label: 'Curate From Classics', value: 'search' },
              { label: 'Curate From Scratch', value: 'scratch' },
            ]}
            active={premiumView}
            onChange={setPremiumView}
          />
        </div>
      )}

      {!premiumUser || premiumView === 'search'
        ? renderSearchView()
        : <ScratchBookForm onSubmitBook={handleCuratedBookSubmit} />}
    </div>
  );
};

export default SearchOwnBooks;